import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { IN_VIEW_MARGIN } from './connections';
import { useRevealed } from './revealStore';
import { useReducedMotionPref } from './useReducedMotionPref';

interface RevealProps {
  /** Classes for the outer, never-transformed wrapper (safe to measure lines against). */
  className?: string;
  innerClassName?: string;
  /**
   * Wait for a connection line to mark this key before animating in
   * (see reveal.ts). Undefined reveals on mount, after `delay`.
   */
  revealKey?: string;
  /** Reveal when scrolled into view instead (once), using the shared in-view margin. */
  inView?: boolean;
  delay?: number;
  duration?: number;
  /** Starting offset to slide in from. */
  from?: { x?: number; y?: number };
  children: ReactNode;
}

export function Reveal({
  className,
  innerClassName,
  revealKey,
  inView = false,
  delay = 0,
  duration = 0.6,
  from = {},
  children,
}: RevealProps) {
  const revealed = useRevealed(revealKey);
  const reduced = useReducedMotionPref();

  const hidden = { opacity: 0, x: from.x ?? 0, y: from.y ?? 0 };
  const shown = { opacity: 1, x: 0, y: 0 };
  const transition = reduced
    ? { duration: 0, delay: 0 }
    : { duration, delay, ease: 'easeOut' as const };

  if (inView && !reduced) {
    return (
      <div className={className}>
        <motion.div
          className={innerClassName}
          initial={hidden}
          whileInView={shown}
          viewport={{ once: true, margin: IN_VIEW_MARGIN }}
          transition={transition}
        >
          {children}
        </motion.div>
      </div>
    );
  }

  return (
    <div className={className}>
      <motion.div
        className={innerClassName}
        initial={reduced ? false : hidden}
        animate={revealed || reduced ? shown : hidden}
        transition={transition}
      >
        {children}
      </motion.div>
    </div>
  );
}
